



import React, { useState, useMemo } from 'react';
import { Search, X, Check, Filter } from 'lucide-react';
import { Schedule } from '../../types';
import VirtualList from '../atoms/VirtualList';

interface ScheduleSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (scheduleId: string) => void;
  schedules: Schedule[];
  title?: string;
  excludeId?: string;
}

const ScheduleSelectionModal: React.FC<ScheduleSelectionModalProps> = (props) => {
  if (!props.isOpen) return null;
  return <ScheduleSelectionModalInner {...props} />;
};

const ScheduleSelectionModalInner: React.FC<ScheduleSelectionModalProps> = ({
    onClose,
    onSelect,
    schedules,
    title = "Select Schedule",
    excludeId
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredSchedules = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return schedules
      .filter(s => !excludeId || s.scheduleId !== excludeId)
      .filter(s =>
        s.name.toLowerCase().includes(term) ||
        s.scheduleId.toLowerCase().includes(term) ||
        s.group.toLowerCase().includes(term)
    );
  }, [schedules, searchTerm, excludeId]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl h-[70vh] flex flex-col overflow-hidden border border-slate-200">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-100 flex gap-4 bg-white flex-shrink-0">
            <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                    type="text"
                    placeholder="Search by name, ID or group..."
                    className="w-full pl-10 pr-4 py-2 bg-white text-slate-900 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all placeholder:text-slate-400"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-slate-600 text-sm">
                <Filter size={16} />
                <span className="font-medium">{filteredSchedules.length}</span>
            </div>
        </div>

        {/* List with Virtualization */}
        <div className="flex-1 overflow-hidden bg-slate-50 p-2 relative">
            {filteredSchedules.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                    No schedules found.
                </div>
            ) : (
                <VirtualList<Schedule>
                    items={filteredSchedules}
                    height="100%"
                    itemHeight={64} // Approx height of each list item including margin
                    renderItem={(schedule) => (
                        <div key={schedule.scheduleId} className="pb-1">
                            <button
                                onClick={() => {
                                    onSelect(schedule.scheduleId);
                                    onClose();
                                }}
                                className="w-full text-left p-3 rounded-lg border border-transparent hover:border-teal-200 hover:bg-teal-50 hover:shadow-sm bg-white transition-all group flex items-center justify-between h-[60px]"
                            >
                                <div>
                                    <div className="font-semibold text-slate-800 text-sm truncate">{schedule.name}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 uppercase font-bold">{schedule.group}</span>
                                        <span className="text-[10px] font-mono text-slate-400 truncate max-w-[150px]">{schedule.scheduleId}</span>
                                    </div>
                                </div>
                                <div className="opacity-0 group-hover:opacity-100 text-teal-600 transition-opacity">
                                    <Check size={18} />
                                </div>
                            </button>
                        </div>
                    )}
                />
            )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleSelectionModal;
